import {
  pixelPolylineLength,
  pixelPolygonArea,
  pixelsToFeet,
  pixelsSqToSF,
  type PixelPoint,
  type SheetScale,
} from '@otto/onscreen-engine';
import { COLORS } from './styles.ts';
import type { TakeoffItem, Tool } from './helpers.ts';

interface Props {
  width: number;
  height: number;
  renderScale: number;
  scale: SheetScale | null;
  items: readonly TakeoffItem[];
  selectedItemId: string | null;
  hoveredItemId: string | null;
  inProgress: PixelPoint[];
  cursor: PixelPoint | null;
  tool: Tool;
  calibP1: PixelPoint | null;
  calibP2: PixelPoint | null;
  onItemClick: (id: string) => void;
  onItemHover: (id: string | null) => void;
  showPolygonCloseHint: boolean;
}

export function GeometryOverlay({
  width,
  height,
  renderScale,
  scale,
  items,
  selectedItemId,
  hoveredItemId,
  inProgress,
  cursor,
  tool,
  calibP1,
  calibP2,
  onItemClick,
  onItemHover,
  showPolygonCloseHint,
}: Props) {
  if (width === 0 || height === 0) return null;

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    >
      {items.map((it) => (
        <ItemShape
          key={it.id}
          item={it}
          renderScale={renderScale}
          scale={scale}
          selected={selectedItemId === it.id}
          hovered={hoveredItemId === it.id}
          onClick={() => onItemClick(it.id)}
          onEnter={() => onItemHover(it.id)}
          onLeave={() => onItemHover(null)}
        />
      ))}

      {tool === 'linear' && inProgress.length > 0 && (
        <InProgressLinear points={inProgress} cursor={cursor} renderScale={renderScale} />
      )}
      {tool === 'polygon' && inProgress.length > 0 && (
        <InProgressPolygon
          points={inProgress}
          cursor={cursor}
          renderScale={renderScale}
          showCloseHint={showPolygonCloseHint}
        />
      )}

      {tool === 'calibrate' && calibP1 && (
        <CalibPoint x={calibP1.x * renderScale} y={calibP1.y * renderScale} label="1" />
      )}
      {tool === 'calibrate' && calibP2 && (
        <CalibPoint x={calibP2.x * renderScale} y={calibP2.y * renderScale} label="2" />
      )}
      {tool === 'calibrate' && calibP1 && calibP2 && (
        <line
          x1={calibP1.x * renderScale}
          y1={calibP1.y * renderScale}
          x2={calibP2.x * renderScale}
          y2={calibP2.y * renderScale}
          stroke={COLORS.accent}
          strokeWidth={2}
          opacity={0.85}
        />
      )}
    </svg>
  );
}

function ItemShape({
  item,
  renderScale,
  scale,
  selected,
  hovered,
  onClick,
  onEnter,
  onLeave,
}: {
  item: TakeoffItem;
  renderScale: number;
  scale: SheetScale | null;
  selected: boolean;
  hovered: boolean;
  onClick: () => void;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const stroke = selected ? COLORS.green : hovered ? COLORS.accent : COLORS.purple;
  const strokeWidth = selected || hovered ? 2.5 : 2;
  const opacity = hovered ? 1 : 0.92;
  const labelText = computeLabel(item, scale);
  const labelPos = labelPosition(item, renderScale);

  if (item.kind === 'count') {
    return (
      <g
        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        onClick={onClick}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {item.points.map((p, i) => (
          <circle
            key={i}
            cx={p.x * renderScale}
            cy={p.y * renderScale}
            r={6}
            fill={stroke}
            stroke="#0D0F1C"
            strokeWidth={1.5}
            opacity={opacity}
          />
        ))}
        {labelPos && labelText && (
          <Label x={labelPos.x} y={labelPos.y} text={labelText} color={stroke} />
        )}
      </g>
    );
  }

  const pts = item.points.map((p) => `${p.x * renderScale},${p.y * renderScale}`).join(' ');

  if (item.kind === 'polygon') {
    return (
      <g
        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        onClick={onClick}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <polygon
          points={pts}
          fill={stroke}
          fillOpacity={selected ? 0.18 : 0.1}
          stroke={stroke}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
        {labelPos && labelText && (
          <Label x={labelPos.x} y={labelPos.y} text={labelText} color={stroke} />
        )}
      </g>
    );
  }

  // linear
  return (
    <g style={{ pointerEvents: 'auto', cursor: 'pointer' }} onClick={onClick}>
      <polyline
        points={pts}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        opacity={opacity}
      />
      {item.points.map((p, i) => (
        <circle
          key={i}
          cx={p.x * renderScale}
          cy={p.y * renderScale}
          r={3}
          fill={stroke}
          opacity={opacity}
        />
      ))}
      {labelPos && labelText && (
        <Label x={labelPos.x} y={labelPos.y} text={labelText} color={stroke} />
      )}
    </g>
  );
}

function InProgressLinear({
  points,
  cursor,
  renderScale,
}: {
  points: PixelPoint[];
  cursor: PixelPoint | null;
  renderScale: number;
}) {
  const pts = points.map((p) => `${p.x * renderScale},${p.y * renderScale}`).join(' ');
  const last = points[points.length - 1];
  return (
    <g>
      {points.length >= 2 && (
        <polyline points={pts} fill="none" stroke={COLORS.accent} strokeWidth={2} />
      )}
      {cursor && last && (
        <line
          x1={last.x * renderScale}
          y1={last.y * renderScale}
          x2={cursor.x * renderScale}
          y2={cursor.y * renderScale}
          stroke={COLORS.accent}
          strokeWidth={2}
          strokeDasharray="4 4"
          opacity={0.7}
        />
      )}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x * renderScale}
          cy={p.y * renderScale}
          r={4}
          fill={COLORS.accent}
          stroke="#0D0F1C"
          strokeWidth={1.5}
        />
      ))}
    </g>
  );
}

function InProgressPolygon({
  points,
  cursor,
  renderScale,
  showCloseHint,
}: {
  points: PixelPoint[];
  cursor: PixelPoint | null;
  renderScale: number;
  showCloseHint: boolean;
}) {
  const pts = points.map((p) => `${p.x * renderScale},${p.y * renderScale}`).join(' ');
  const first = points[0];
  const last = points[points.length - 1];
  // Closing line is shown solid+green when the cursor is in snap range,
  // otherwise dashed at lower opacity as a faint hint that closing is possible.
  const canClose = points.length >= 3 && first && cursor;
  return (
    <g>
      {points.length >= 2 && (
        <polyline points={pts} fill="none" stroke={COLORS.accent} strokeWidth={2} />
      )}
      {cursor && last && (
        <line
          x1={last.x * renderScale}
          y1={last.y * renderScale}
          x2={cursor.x * renderScale}
          y2={cursor.y * renderScale}
          stroke={showCloseHint ? COLORS.green : COLORS.accent}
          strokeWidth={2}
          strokeDasharray="4 4"
          opacity={showCloseHint ? 0.95 : 0.7}
        />
      )}
      {canClose && (
        <line
          x1={cursor.x * renderScale}
          y1={cursor.y * renderScale}
          x2={first.x * renderScale}
          y2={first.y * renderScale}
          stroke={showCloseHint ? COLORS.green : COLORS.accent}
          strokeWidth={showCloseHint ? 2 : 1}
          strokeDasharray={showCloseHint ? undefined : '2 4'}
          opacity={showCloseHint ? 0.95 : 0.4}
        />
      )}
      {points.map((p, i) => {
        const isFirst = i === 0;
        const fill = isFirst && showCloseHint ? COLORS.green : isFirst ? COLORS.green : COLORS.accent;
        const r = isFirst && showCloseHint ? 6 : 4;
        const ring = isFirst && showCloseHint ? 2.5 : 1.5;
        return (
          <circle
            key={i}
            cx={p.x * renderScale}
            cy={p.y * renderScale}
            r={r}
            fill={fill}
            stroke="#0D0F1C"
            strokeWidth={ring}
          />
        );
      })}
    </g>
  );
}

function CalibPoint({ x, y, label }: { x: number; y: number; label: string }) {
  return (
    <g>
      <circle cx={x} cy={y} r={9} fill={COLORS.accent} stroke="#fff" strokeWidth={2} />
      <text
        x={x}
        y={y + 3}
        textAnchor="middle"
        fontSize={10}
        fontFamily="'JetBrains Mono', monospace"
        fontWeight={700}
        fill="#0D0F1C"
      >
        {label}
      </text>
    </g>
  );
}

function Label({ x, y, text, color }: { x: number; y: number; text: string; color: string }) {
  // Slight outline (paint-order trick) for legibility against light/dark backgrounds.
  return (
    <text
      x={x}
      y={y}
      fontSize={11}
      fontFamily="'JetBrains Mono', monospace"
      fontWeight={600}
      fill={color}
      stroke="#0D0F1C"
      strokeWidth={3}
      paintOrder="stroke"
      style={{ pointerEvents: 'none' }}
    >
      {text}
    </text>
  );
}

function computeLabel(item: TakeoffItem, scale: SheetScale | null): string {
  if (item.kind === 'count') {
    return `${item.points.length}`;
  }
  if (!scale) return '';
  if (item.kind === 'linear') {
    const lf = pixelsToFeet(pixelPolylineLength(item.points), scale);
    return `${lf.toFixed(1)} LF`;
  }
  if (item.kind === 'polygon') {
    const sf = pixelsSqToSF(pixelPolygonArea(item.points), scale);
    return `${sf.toFixed(0)} SF`;
  }
  return '';
}

function labelPosition(item: TakeoffItem, renderScale: number): { x: number; y: number } | null {
  if (item.points.length === 0) return null;
  if (item.kind === 'count') {
    // Above the first marker
    const p = item.points[0];
    return { x: p.x * renderScale + 10, y: p.y * renderScale - 8 };
  }
  // Centroid of points
  let sx = 0;
  let sy = 0;
  for (const p of item.points) {
    sx += p.x;
    sy += p.y;
  }
  return {
    x: (sx / item.points.length) * renderScale,
    y: (sy / item.points.length) * renderScale,
  };
}
